import React, { useEffect, useState, useReducer } from 'react';
import SwiperClass from 'swiper';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import { LayoutProps } from '../../layouts/layout.props';
import { Header } from '../../header';
import { ProgressHeader } from '../../ProgressHeader';
import { useSubmission } from '@/components/use.submission';
import SubmitModal from '@/components/SubmitModal';
import {
  ACTIONS,
  SubmissionsState,
  SubmissionsAction,
} from '@/components/layouts/layout.props';
import { DesignSkillStackStartPage } from './new-start-page';
import DesignSkillStackResults from './index';
import UserInfoSlide from './components/user-info-slide';
import QuestionSlide from './components/questionSlide';
import { withAuth } from '../../with.auth';

const reducer = (
  submissions: SubmissionsState[],
  action: SubmissionsAction
) => {
  switch (action.type) {
    case ACTIONS.ADD_SUBMISSION:
      let replaced = false;
      const updated = submissions.map((submission: SubmissionsState) => {
        if (submission.field === action.payload.field) {
          replaced = true;
          return action.payload;
        } else {
          return submission;
        }
      });
      if (replaced) {
        return updated;
      } else {
        return [...updated, action.payload];
      }
    case ACTIONS.DELETE_SUBMISSIONS:
      return [];
    default:
      return submissions;
  }
};

const DesignSkillStackLayout: React.FC<LayoutProps> = (props) => {
  const [swiper, setSwiper] = useState<SwiperClass>();
  const { id, design, startPage, endPage, fields, isLive } = props.form;
  const { customFormJson } = props;
  const [submissionFlag, setSubmissionFlag] = useState(false); // flag to whether submit the submission or not
  const currentSubmission = useSubmission(id as string);
  const { startSubmission, finish, setField } = currentSubmission;
  const [submissions, dispatch] = useReducer(reducer, []); // overall submissions
  // used to reset answers of each question when user resets form
  // field.tsx - formik doesn't reset the form unless its intialValues prop changes
  const [submissionCounter, setSubmissionCounter] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lastSlideIndex, _setLastSlideIndex] = useState<number>(
    customFormJson.numberOfSlides * 2 + 4
  );
  const [numberOfSlides, setNumberOfSlides] = useState<number>(
    customFormJson.numberOfSlides
  );
  const [fieldsBySlide, setFieldsBySlide] = useState([]);
  const [submissionId, setSubmissionId] = useState<string>();

  //track total questions
  const totalQuestions = fields.filter(
    (field) => field.type !== 'hidden'
  ).length;

  const formatSubData = (): any => {
    const formatted = submissions.map((item) => {
      const field = fields.find((field) => field.id === item.field);
      return { field: field, id: item.field, value: item.data };
    });
    return {
      submission: {
        fields: formatted,
      },
    };
  };

  useEffect(() => {
    if (customFormJson.numberOfSlides) {
      setNumberOfSlides(customFormJson.numberOfSlides);
    }
  }, [customFormJson]);

  useEffect(() => {
    if (numberOfSlides) {
      const fieldsBySlideNumberArr: any = [];
      for (let i = 1; i <= numberOfSlides; i++) {
        const slideQuestions = customFormJson.questions.filter(
          (question: any) => question.slideNumber === i
        );
        const slideFields = slideQuestions.map((question: any) => {
          const fieldInSlide = fields.find(
            (field: any) => field.id === question.questionId
          );
          return fieldInSlide;
        });

        fieldsBySlideNumberArr.push(slideFields);

        if (i === numberOfSlides) {
          setFieldsBySlide(fieldsBySlideNumberArr);
        }
      }
    }
  }, [numberOfSlides]);

  useEffect(() => {
    document.title = props.form.title;
  }, []);

  useEffect(() => {
    const submitSubmission = async () => {
      const s: { id: string; token: string } | undefined =
        await startSubmission();
      if (s) {
        setSubmissionId(s.id);
        await setField('', submissions, s).catch((err) => console.error(err));
        await finish(s);
      }
      setProgress(100);
      setShowProgress(false);
      setSubmissionFlag(false);
      // saving could be really fast so this prevents saving spinner from flashing
      await new Promise((r) => setTimeout(r, 1000));
      goLast();
    };
    if (submissionFlag) {
      submitSubmission();
    }
  }, [submissionFlag]);

  // mui text input (textarea, textfield) does not go back to its default value even though we pass in empty values and re-render the components
  // this hook ensures that when the form is resetted, the values in the text inputs do not hold the previous user inputs
  useEffect(() => {
    if (submissionCounter !== 0) {
      Array.from(document.getElementsByClassName('MuiInputBase-input')).forEach(
        (textfield) => {
          const converted = textfield as HTMLInputElement;
          converted.value = '';
        }
      );
    }
  }, [submissionCounter]);

  const goNext = () => {
    if (!swiper) return;
    swiper.allowSlideNext = true;
    if (startPage.show && swiper.activeIndex === 0) {
      swiper.slideTo(1);
      startPage.show;
    } else {
      swiper.slideNext();
    }

    // TODO - Fix progress bar to factor in number of custom slides/mulitply question slides
    if (swiper.activeIndex === 1) {
      //setShowProgress(true);
      setProgress(2);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlideNext = false;
  };
  const goPrev = () => {
    if (!swiper) return;

    swiper.allowSlidePrev = true;
    swiper.slidePrev();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlidePrev = false;
  };
  const goBeginning = () => {
    if (!swiper) return;

    setProgress(0);
    setShowProgress(!startPage.show);
    dispatch({
      type: ACTIONS.DELETE_SUBMISSIONS,
      payload: { field: '', data: '' },
    });
    setSubmissionCounter(submissionCounter + 1);
    swiper.allowSlidePrev = true;
    swiper.slideTo(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlidePrev = false;
  };
  const goLast = () => {
    if (!swiper) return;

    swiper.allowSlideNext = true;
    swiper.slideNext();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlideNext = false;
  };

  const swiperConfig: SwiperProps = {
    direction: 'horizontal',
    allowSlideNext: false,
    allowSlidePrev: false,
    preventClicks: false,
    noSwiping: false,
    allowTouchMove: false,
    preventClicksPropagation: false,
    updateOnWindowResize: true,
  };

  // prevent user from going forward from pressing enter key
  // however it will allow enter key to go through if the event came from mui textarea
  // this is to allow the textarea to add new lines
  const handleEnterKey = async (event: any) => {
    if (
      event.key === 'Enter' &&
      !event.target.className.includes('MuiInputBase-inputMultiline')
    ) {
      event.preventDefault();
    }
  };

  return (
    <>
      {submissionFlag && <SubmitModal design={design} />}
      <div
        className={'swiper-container'}
        style={{
          background: design.colors.background,
        }}
        onKeyDown={handleEnterKey}
      >
        <Header design={design} />
        {showProgress ? (
          <ProgressHeader
            progressValue={progress}
            steps={fields.length}
            design={design}
            reset={goBeginning}
          />
        ) : null}

        <Swiper
          autoHeight // for some reason causes speedup transition for start page?
          {...swiperConfig}
          onSwiper={(next) => {
            setSwiper(next);
          }}
        >
          <div className='swiper-wrapper'>
            {[
              startPage.show ? (
                <SwiperSlide key={'start'}>
                  <DesignSkillStackStartPage design={design} next={goNext} />
                </SwiperSlide>
              ) : undefined,
              ...fieldsBySlide
                .map((fields, i) => {
                  if (i === 0) {
                    return (
                      <SwiperSlide key={i} className={'slide'}>
                        <UserInfoSlide
                          design={design}
                          fields={fields}
                          prev={goPrev}
                          next={goNext}
                          lastSlideIndex={lastSlideIndex}
                          submissionCount={submissionCounter}
                          dispatch={dispatch}
                          slideIndex={i}
                          totalQuestions={totalQuestions}
                        />
                      </SwiperSlide>
                    );
                  } else {
                    return (
                      <SwiperSlide key={i} className={'slide'}>
                        <QuestionSlide
                          design={design}
                          fields={fields}
                          prev={goPrev}
                          next={goNext}
                          lastSlideIndex={numberOfSlides}
                          slideIndex={i}
                          submissionCount={submissionCounter}
                          dispatch={dispatch}
                          totalQuestions={totalQuestions}
                          setSubmissionFlag={setSubmissionFlag}
                          numberOfSlides={numberOfSlides}
                        />
                      </SwiperSlide>
                    );
                  }
                })
                .filter((e) => e !== null),

              endPage.show ? (
                <SwiperSlide key={'end'}>
                  <DesignSkillStackResults
                    design={design}
                    submissionData={formatSubData()}
                    submissionId={submissionId}
                  />
                </SwiperSlide>
              ) : undefined,
            ].filter((e) => !!e)}
          </div>
        </Swiper>
      </div>
    </>
  );
};

export default withAuth(DesignSkillStackLayout);
